import { Box, Modal } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import UploadIconSvg from "../../../../common/svgs/UploadIconSvg";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import ScrollableWrapper from "../common/ScrollableWrapper";
import { useAppStore } from "../../../../providers/AppStore";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import InfoIcon from "../../svg/InfoIcon";
import CommonDatePicker from "../common/CommonDatePicker";
import { format } from "date-fns";
import BinSvg from "../../../../common/svgs/BinSvg";
import RoundedIconContainer from "../common/RoundedIconContainer";
import CommonInputField from "../common/CommonInputField";
import CustomSelect from "../common/CustomSelect";
import {
  getPlatformData,
  GetCostumerBiomarkerStatus,
  UpdateCostumerBiomarkersBatch,
  UploadCostumerBiomarker,
} from "../../../../common/network/Api";
import { platformActions, RootStatePlatform } from "../../store/platform-slice";
import { parseRangeString } from "../../store/helper";
import {
  buildCatalogById,
  isPersisted,
  NormalizedBiomarker,
  normalizeResults,
} from "../../utils/extractorResults";
import { useAuth } from "../../../../providers/AuthProvider";
import { useDispatch, useSelector } from "react-redux";
import PageLoader from "../../../reports/components/UI/PageLoader";
import {
  ExtractorJobResponse,
  UpdateBiomarkerBatchItem,
} from "../../../../common/entities/type";
import UnsavedBiomarkersNotice, {
  unsavedNoticeMessage,
} from "./UnsavedBiomarkersNotice";
import DocumentScanLoader from "./DocumentScanLoader";
import LocalizedNumber from "./LocalizedNumber";
import LoadingTipsCarousel from "./LoadingTipsCarousel";
import { formatDisplayDate } from "../../../../common/utils/date";
import { isValidDate } from "../../../../providers/helperFunctions";
import { useBiomarkersSavedSnackbar } from "../../hooks/useBiomarkersSavedSnackbar";

interface BiomarkerExtractorModalProps {
  extractorModalOpen: boolean;
  setExtractorModalOpen: (open: boolean) => void;
  // Opens the host screen's Review health scores modal. Passed on the Health
  // overview, where saved biomarkers immediately move system scores into
  // "pending approval"; omitted where there is no such modal to open.
  onReviewScores?: () => void;
}

type ExtractorState = {
  measurementDate: string;
  items: NormalizedBiomarker[];
};

const POLL_INTERVAL_MS = 5000; // poll job status every 5s
const POLL_MAX_DURATION_MS = 60 * 60 * 1000; // give up after 1h

const BiomarkerExtractorModal: React.FC<BiomarkerExtractorModalProps> = ({
  extractorModalOpen,
  setExtractorModalOpen,
  onReviewScores,
}) => {
  const currentDate = new Date();
  const theme: Theme = useTheme();
  const [result, setResult] = useState<ExtractorState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCancelledRef = useRef(false);
  const [focusedResult, setFocusedResult] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const {
    ASSelectedClient,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASGetClientBiomarkerTabDataHandler,
  } = useAppStore();
  const { AuthUser } = useAuth();
  const dispatch = useDispatch();
  const biomarkerList = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerList
  );
  const showSavedSnackbar = useBiomarkersSavedSnackbar(onReviewScores);

  // Catalog lookup (id → qualitative/qoptions) from GetAllBiomarkers, used to
  // classify extracted values. Built once from the (cached) catalog list.
  const catalogById = useMemo(
    () => buildCatalogById(biomarkerList),
    [biomarkerList]
  );

  const { persistedItems, rejectedUnitItems, unmatchedItems } = useMemo(() => {
    const items = result?.items ?? [];
    return {
      persistedItems: items.filter(isPersisted),
      rejectedUnitItems: items.filter((i) => i.status === "rejectedUnit"),
      unmatchedItems: items.filter((i) => i.status === "unmatched"),
    };
  }, [result]);

  // Block saving while any qualitative row still has no option chosen.
  const hasUnsetQualitative = persistedItems.some(
    (i) => i.status === "qualitative" && i.displayValue === ""
  );

  // The backend may return an empty measurementDate; require a valid one before
  // saving (every persisted biomarker is stamped with it).
  const hasValidMeasurementDate =
    !!result?.measurementDate && isValidDate(new Date(result.measurementDate));

  // Stop any in-flight polling if the component unmounts.
  useEffect(() => {
    return () => {
      pollCancelledRef.current = true;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  if (!extractorModalOpen) return null;

  const stopPolling = () => {
    pollCancelledRef.current = true;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const showDetectError = () => {
    ASSetOpenSnackBar(true);
    ASSetSnackBarMessage({
      title: `We couldn’t detect any biomarkers.`,
      message: `Please check the text clarity and try with a different file.`,
    });
    ASSetTypeSnackBar("alert");
  };

  // Recursively polls the job status until Done/Failed, the 1h cap, or cancel.
  const pollJobStatus = async (jobId: string, startedAt: number) => {
    if (pollCancelledRef.current) return;
    try {
      const job: ExtractorJobResponse = await GetCostumerBiomarkerStatus(jobId);
      if (pollCancelledRef.current) return;

      if (job?.status === "Done") {
        stopPolling();
        const jobResult = job.result;
        if (jobResult && jobResult.results.length > 0) {
          setResult({
            measurementDate: jobResult.measurementDate,
            items: normalizeResults(jobResult.results, catalogById),
          });
          setActiveTab(1);
        } else {
          showDetectError();
        }
        setIsProcessing(false);
        return;
      }

      if (job?.status === "Failed") {
        stopPolling();
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: `Error processing file`,
          message:
            job.error ||
            job.message ||
            "We couldn’t process your document. Please try again with a different file.",
        });
        ASSetTypeSnackBar("alert");
        setIsProcessing(false);
        return;
      }

      // Still running — stop after the 1h cap, otherwise schedule the next poll.
      if (Date.now() - startedAt >= POLL_MAX_DURATION_MS) {
        stopPolling();
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: `Processing is taking longer than expected`,
          message: `Please try again later.`,
        });
        ASSetTypeSnackBar("alert");
        setIsProcessing(false);
        return;
      }
      pollTimeoutRef.current = setTimeout(
        () => pollJobStatus(jobId, startedAt),
        POLL_INTERVAL_MS
      );
    } catch (err: any) {
      if (pollCancelledRef.current) return;
      stopPolling();
      console.warn(err);
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: `Error processing file`,
        message: err.message || "An unexpected error occurred.",
      });
      ASSetTypeSnackBar("alert");
      setIsProcessing(false);
    }
  };

  const handleChangeDate = (newValue: Date) => {
    if (newValue === null) {
      return;
    }
    const today = new Date();
    if (newValue > today) {
      setErrorMessage("Future dates are not allowed.");
    } else {
      setErrorMessage("");
    }
    const formattedValue = format(new Date(newValue), "yyyy-MM-dd");
    setResult((prev) =>
      prev ? { ...prev, measurementDate: formattedValue } : prev
    );
  };

  const handleChangeValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: number
  ) => {
    let newValue = event.target.value;
    if (newValue.length > 8) {
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: `Maximum limit of characters reached.`,
        message: `Only 8 characters allowed.`,
      });
      ASSetTypeSnackBar("alert");
      return;
    }
    if (newValue.startsWith(".")) {
      newValue = "0" + newValue;
    }
    if (
      newValue.length > 2 &&
      newValue.startsWith("0") &&
      !newValue.startsWith("0.")
    ) {
      newValue = newValue.replace(/^0+/, ""); // Remove leading zeros except for "0."
    }
    setResult((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((item) =>
              item.key === key ? { ...item, displayValue: newValue } : item
            ),
          }
        : prev
    );
  };

  // Qualitative rows store the selected qoption string directly — no numeric
  // sanitization.
  const handleChangeQualitative = (selected: string | null, key: number) => {
    setResult((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((item) =>
              item.key === key
                ? { ...item, displayValue: selected ?? "" }
                : item
            ),
          }
        : prev
    );
  };

  const handleDeleteItem = (key: number) => {
    setResult((prev) => {
      if (!prev) return prev;
      const updatedItems = prev.items.filter((item) => item.key !== key);
      if (updatedItems.length === 0) {
        setActiveTab(0);
        return null;
      }
      return { ...prev, items: updatedItems };
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = async (file: File) => {
    setResult(null);
    setIsDragging(false);
    if (isProcessing) {
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: `A file is already being processed.`,
        message: `Please wait until the current upload is completed.`,
      });
      ASSetTypeSnackBar("alert");
      return; // Prevent multiple uploads while processing
    }
    try {
      if (
        !(file.type.startsWith("image/") || file.type === "application/pdf")
      ) {
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: `This file format is not supported.`,
          message: `Please upload a .PDF, .JPG, or .PNG file.`,
        });
        ASSetTypeSnackBar("alert");
        return;
      }
      if (!ASSelectedClient) {
        return;
      }
      setIsProcessing(true);
      pollCancelledRef.current = false;

      // The upload now starts an async job and returns its id; we then poll the
      // status endpoint every 5s until it resolves (Done/Failed).
      const uploadResponse = await UploadCostumerBiomarker(
        ASSelectedClient.id,
        file
      );
      const jobId: string | undefined = uploadResponse?.jobId;

      if (!jobId) {
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: `Error processing file`,
          message: `We couldn’t start processing your document. Please try again.`,
        });
        ASSetTypeSnackBar("alert");
        setIsProcessing(false);
        return;
      }

      pollJobStatus(jobId, Date.now());
    } catch (err: any) {
      console.warn(err);
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: `Error processing file`,
        message: err.message || "An unexpected error occurred.",
      });
      ASSetTypeSnackBar("alert");
      setIsProcessing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleCloseFunction = () => {
    stopPolling();
    setExtractorModalOpen(false);
    setActiveTab(0);
    setIsProcessing(false);
    setResult(null);
  };

  const handleSaveBiomarkers = async () => {
    try {
      setIsLoading(true);
      if (!ASSelectedClient || !result || !hasValidMeasurementDate) {
        setIsLoading(false);
        return;
      }

      // Persist numeric, qualitative, and (read-only) range items via the batch
      // UpdateBiomarkers endpoint; rejected/unmatched are excluded. Each item
      // carries userId + the shared measurement date, then newValue (numeric),
      // qvalue (qualitative), or the parsed range_* bounds (range). Values are
      // already in the biomarker's canonical unit, so no unit is sent.
      const payload: UpdateBiomarkerBatchItem[] = result.items
        .filter(isPersisted)
        .map((item) => {
          const base = {
            biomarkerId: item.biomarkerID as string,
            userId: ASSelectedClient.id,
            date: result.measurementDate,
          };
          if (item.status === "qualitative") {
            return { ...base, qvalue: item.displayValue };
          }
          if (item.status === "range") {
            return { ...base, ...parseRangeString(item.displayValue) };
          }
          return { ...base, newValue: Number(item.displayValue) };
        });

      await UpdateCostumerBiomarkersBatch(payload);

      const [, dataPlatform] = await Promise.all([
        ASGetClientBiomarkerTabDataHandler(ASSelectedClient.id),
        getPlatformData(ASSelectedClient.id, AuthUser.role),
      ]);
      dispatch(platformActions.updateDataPlatformServer(dataPlatform));
      handleCloseFunction();
      setIsLoading(false);

      // Prompts for the score review when the save left something pending;
      // falls back to the plain toast otherwise (see the hook).
      showSavedSnackbar({
        report: dataPlatform,
        reviewTitle: "Biomarkers saved",
        fallback: {
          title: "",
          message: "The biomarker lab results have been updated successfully.",
        },
      });
    } catch (error: any) {
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: `Error ${error.status}`,
        message: `Failed to save biomarker`,
      });
      ASSetTypeSnackBar("alert");
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={extractorModalOpen}
      onClose={handleCloseFunction}
      disableEnforceFocus
    >
      <>
        {activeTab === 0 && (
          <div style={styles.modalContainer(theme)}>
            <div style={styles.modalHeaderContainer}>
              <div style={styles.modalHeaderTitle(theme)}>
                Upload biomarkers
              </div>
              <div onClick={handleCloseFunction} style={styles.iconWrapper}>
                <XIconSvg />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                minHeight: 0,
                marginRight: "-10px",
              }}
            >
              <ScrollableWrapper>
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  style={styles.modalInput(theme, isDragging)}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isProcessing ? (
                    <div style={styles.modalInputContent}>
                      <DocumentScanLoader size={48} />
                      <LoadingTipsCarousel />
                    </div>
                  ) : (
                    <div style={styles.modalInputContent}>
                      <UploadIconSvg
                        size={24}
                        color={theme.colors_ds.NEUTRAL_ICON_WEAK}
                      />
                      <div style={styles.modalInputTitle(theme)}>
                        Upload a PDF or image (JPG/PNG) of your lab results
                      </div>
                      <div style={styles.modalInputSubTitle(theme)}>
                        Drag and drop your file here, or click inside the box to
                        select one.
                      </div>
                      <div style={styles.modalInputSubTitle(theme)}>
                        We’ll automatically extract your biomarker values and
                        associated date.
                      </div>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  style={{ display: "none" }}
                  onChange={handleInputChange}
                />
              </ScrollableWrapper>
            </div>
          </div>
        )}

        {activeTab === 1 && (
          <div style={styles.modalContainer(theme)}>
            <div style={styles.modalHeaderContainer}>
              <div style={styles.modalHeaderTitle(theme)}>
                Review extracted lab results
              </div>
              <div onClick={handleCloseFunction} style={styles.iconWrapper}>
                <XIconSvg />
              </div>
            </div>
            {isLoading && <PageLoader />}
            {!isLoading && (
              <>
                <ScrollableWrapper>
                  <div style={styles.infoTextContainer(theme)}>
                    <div>
                      <InfoIcon size={12} />
                    </div>
                    <div style={styles.infoText(theme)}>
                      The following data was detected from your uploaded
                      document. You can edit the value or date, or remove any
                      field you don’t want to save.
                    </div>
                  </div>
                  {result ? (
                    <div>
                      <div style={styles.resultTitle(theme)}>Date</div>
                      <div style={styles.dateCard(theme)}>
                        <CommonDatePicker
                          label={"Date"}
                          value={result.measurementDate}
                          handleChange={handleChangeDate}
                          maxValue={new Date(currentDate)}
                        />
                        <Box
                          sx={{
                            color: theme.colors_ds.DANGER_GRAPH_DEFICIT,
                            fontSize: "12px",
                            marginTop: "4px",
                          }}
                        >
                          {errorMessage ||
                            (!hasValidMeasurementDate
                              ? "Add the measurement date to save these results."
                              : "")}
                        </Box>
                      </div>

                      <UnsavedBiomarkersNotice
                        title={"Couldn’t convert units"}
                        message={unsavedNoticeMessage(
                          rejectedUnitItems.length,
                          "we couldn’t convert to the units we use"
                        )}
                        items={rejectedUnitItems.map((item) => ({
                          name: item.displayName,
                          value: item.displayValue,
                          unit: item.displayUnit,
                        }))}
                      />
                      <UnsavedBiomarkersNotice
                        title={"Not matched to our database"}
                        message={unsavedNoticeMessage(
                          unmatchedItems.length,
                          "we couldn’t match to our database"
                        )}
                        items={unmatchedItems.map((item) => ({
                          name: item.displayName,
                          value: item.displayValue,
                          unit: item.displayUnit,
                        }))}
                      />

                      {persistedItems.length > 0 && (
                        <>
                          <div style={styles.resultTitle(theme)}>
                            {`Blood analysis (${persistedItems.length} biomarkers)`}
                          </div>
                          <div style={styles.resultCardContainer}>
                            {persistedItems.map((el) => (
                              <div key={el.key}>
                                <div
                                  style={{
                                    ...styles.resultCard(
                                      theme,
                                      String(el.key) === focusedResult
                                    ),
                                    borderColor: el.originalValue
                                      ? theme.colors_ds.WARNING_GRAPH_MAIN
                                      : theme.colors_ds.NEUTRAL_BORDER_WEAK,
                                  }}
                                >
                                  <div style={{ flex: 1.3 }}>
                                    <div style={styles.label(theme, false)}>
                                      Name
                                    </div>
                                    <div style={styles.value(theme, false)}>
                                      {el.displayName}
                                    </div>
                                  </div>
                                  <div style={{ flex: 0.5 }}>
                                    <div style={styles.label(theme, false)}>
                                      Date
                                    </div>
                                    <div style={styles.value(theme, false)}>
                                      {formatDisplayDate(
                                        result.measurementDate
                                      )}
                                    </div>
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    {el.status === "qualitative" ? (
                                      <CustomSelect
                                        label={"Value"}
                                        options={el.qoptions ?? []}
                                        value={el.displayValue || null}
                                        onChange={(selected) =>
                                          handleChangeQualitative(
                                            selected,
                                            el.key
                                          )
                                        }
                                        borderless
                                        placeholder={"Select option"}
                                      />
                                    ) : el.status === "range" ? (
                                      <>
                                        <div style={styles.label(theme, false)}>
                                          Value
                                        </div>
                                        <div style={styles.value(theme, false)}>
                                          {el.displayValue}
                                          {el.displayUnit
                                            ? ` ${el.displayUnit}`
                                            : ""}
                                        </div>
                                      </>
                                    ) : (
                                      <CommonInputField
                                        label={
                                          el.originalValue
                                            ? "Value (converted)"
                                            : "Value"
                                        }
                                        type={"numeric"}
                                        inputWidth={60}
                                        unit={el.displayUnit}
                                        handleChange={(e) =>
                                          handleChangeValue(e, el.key)
                                        }
                                        value={el.displayValue}
                                        setFocusedIndex={setFocusedResult}
                                        name={String(el.key)}
                                      />
                                    )}
                                  </div>
                                  <RoundedIconContainer
                                    handleClick={() => handleDeleteItem(el.key)}
                                  >
                                    <BinSvg
                                      color={theme.colors_ds.PRIMARY_ICON_MAIN}
                                    />
                                  </RoundedIconContainer>
                                </div>
                                {el.originalValue && (
                                  <div style={styles.originalValueHint(theme)}>
                                    Original detected value:{" "}
                                    <LocalizedNumber value={el.originalValue} />{" "}
                                    {el.originalUnit}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div>No results available</div>
                  )}
                </ScrollableWrapper>
                <div style={styles.bottomButtonContainer}>
                  <SecondaryButton
                    label={"Reset"}
                    onClick={() => {
                      setActiveTab(0);
                      setResult(null);
                    }}
                  />
                  <PrimaryButton
                    label={"Confirm"}
                    onClick={handleSaveBiomarkers}
                    isDisable={
                      errorMessage !== "" ||
                      persistedItems.length === 0 ||
                      hasUnsetQualitative ||
                      !hasValidMeasurementDate
                    }
                  />
                </div>
              </>
            )}
          </div>
        )}
      </>
    </Modal>
  );
};

export default BiomarkerExtractorModal;

const styles = {
  modalContainer: (theme: any) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "24px",
    width: "80%",
    minWidth: "400px",
    maxWidth: "736px",
    height: "90vh", // Allow modal to use most of the screen height
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
  }),
  modalHeaderContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    marginBottom: "32px",
  },
  modalHeaderTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
    flex: 1,
  }),
  iconWrapper: {
    marginTop: "10px",
  },
  modalInput: (theme: any, dragged: boolean) => ({
    border: `1px dashed ${
      dragged
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: dragged ? theme.colorTokens.P[10] : "transparent",
    borderRadius: 8,
    padding: 24,
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    cursor: "pointer",
    maxHeight: 246,
  }),
  modalInputContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
  },
  modalInputTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "24px",
    marginTop: 16,
    marginBottom: 8,
  }),
  modalInputSubTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "20px",
  }),
  bottomButtonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "16px",
    gap: "16px",
  },
  infoTextContainer: (theme: any) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    borderRadius: 8,
    padding: "12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colorTokens.P[10],
  }),
  infoText: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    flex: 1,
  }),
  resultTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    marginTop: 24,
  }),
  dateCard: (theme: any) => ({
    marginTop: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "8px 16px",
    marginBottom: "16px",
    display: "flex",
    gap: "16px",
    alignItems: "center",
  }),
  resultCardContainer: {
    marginTop: "16px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  resultCard: (theme: any, focused: boolean) => ({
    border: `1px solid ${
      focused
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "2px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
  }),
  label: (theme: Theme, isActive: boolean) => ({
    color: isActive
      ? theme.colors_ds.NEUTRAL_TEXT_DEFAULT
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "15px",
  }),
  value: (theme: Theme, isActive: boolean) => ({
    color: isActive
      ? theme.colors_ds.NEUTRAL_TEXT_DEFAULT
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    marginTop: 3,
  }),
  originalValueHint: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "11px",
    lineHeight: "16px",
    paddingLeft: "4px",
  }),
};
